/**
 * Collection paths — ENGINEERING §4. The package imports no firebase; these are strings.
 *
 * `parsePrimitivePath` exists so a server reader never blindly `db.doc(path)`-es a
 * client-supplied string: a `primitivePath` is provenance, to be verified, not trusted.
 */
export const THEMES = 'themes';
export const PRIMITIVES = 'primitives';
export const AUTHORS = 'authors';
export const STORIES = 'stories';
export const ADAPTATIONS = 'adaptations';
export const TRACES = 'traces';
export const VARIANT_INSTANCES = 'variantInstances';
export const SCENE_RENDERS = 'sceneRenders';
export const MEDIUM_RENDERS = 'mediumRenders';
export const MEDIUMS = 'mediums';
export const USERS = 'users';
/** The author id for a signed-in uid. Identity today; the seam is where a future split lands. */
export function authorIdFor(uid) {
    return uid;
}
/** A path segment must be non-empty, slash-free, and not a relative-path token. */
function assertSegment(value, label) {
    if (value === '' || value.includes('/') || value === '.' || value === '..') {
        throw new Error(`collections: invalid ${label} segment: ${JSON.stringify(value)}`);
    }
    return value;
}
function isLegalSegment(value) {
    return value !== undefined && value !== '' && value !== '.' && value !== '..';
}
export function themePath(id) {
    return `${THEMES}/${assertSegment(id, 'theme id')}`;
}
/** The curated global library: world-readable, admin-written. */
export function globalPrimitivePath(id) {
    return `${PRIMITIVES}/${assertSegment(id, 'primitive id')}`;
}
/** An author's private library and its forks: owner-only. */
export function authorPrimitivePath(authorId, id) {
    return `${AUTHORS}/${assertSegment(authorId, 'author id')}/${PRIMITIVES}/${assertSegment(id, 'primitive id')}`;
}
/** The scope decides the library: `'global'` ignores `ownerId` (it is always `'system'`). */
export function primitivePath(scope, ownerId, id) {
    return scope === 'global' ? globalPrimitivePath(id) : authorPrimitivePath(ownerId, id);
}
export function storyPath(authorId, id) {
    return `${AUTHORS}/${assertSegment(authorId, 'author id')}/${STORIES}/${assertSegment(id, 'story id')}`;
}
/** An author's adaptations — a story derived into one medium each. Owner-only, like stories. */
export function adaptationsCollectionPath(authorId) {
    return `${AUTHORS}/${assertSegment(authorId, 'author id')}/${ADAPTATIONS}`;
}
export function adaptationPath(authorId, id) {
    return `${adaptationsCollectionPath(authorId)}/${assertSegment(id, 'adaptation id')}`;
}
/** God-mode traces. Owner-only and backend-written: a trace carries the prompt, and the prompt carries identity. */
export function tracePath(authorId, id) {
    return `${AUTHORS}/${assertSegment(authorId, 'author id')}/${TRACES}/${assertSegment(id, 'trace id')}`;
}
export function variantInstancePath(id) {
    return `${VARIANT_INSTANCES}/${assertSegment(id, 'variant instance id')}`;
}
export function sceneRenderPath(id) {
    return `${SCENE_RENDERS}/${assertSegment(id, 'scene render id')}`;
}
export function mediumRenderPath(id) {
    return `${MEDIUM_RENDERS}/${assertSegment(id, 'medium render id')}`;
}
export function mediumPath(id) {
    return `${MEDIUMS}/${assertSegment(id, 'medium id')}`;
}
export function userPath(uid) {
    return `${USERS}/${assertSegment(uid, 'uid')}`;
}
/**
 * Narrow a client-supplied primitive path to one of its two legal shapes, or `null`.
 *
 * Rejects everything else — wrong roots, extra or missing segments, empty ids, and
 * relative-path tokens — so a caller cannot walk out of the library it is entitled to.
 */
export function parsePrimitivePath(path) {
    const segments = path.split('/');
    if (segments.length === 2) {
        const [root, id] = segments;
        if (root === PRIMITIVES && isLegalSegment(id))
            return { scope: 'global', id };
        return null;
    }
    if (segments.length === 4) {
        const [root, authorId, collection, id] = segments;
        if (root === AUTHORS &&
            collection === PRIMITIVES &&
            isLegalSegment(authorId) &&
            isLegalSegment(id)) {
            return { scope: 'author', authorId, id };
        }
        return null;
    }
    return null;
}
//# sourceMappingURL=collections.js.map